/**
 * This store serves as a 'catch all' location for states that do not directly reflect a user's project design choices
 * This includes states such as which section the user is currently on etc.
 * General states that do not impact a specific project should be retrievable from this store
 */

import { defineStore } from 'pinia'
import useIdGenerator from '@/composables/UseIdGenerator'
import { DisplayObjectState, StickerState, TextState } from '@/types'

export type SnackbarState = 'error' | 'success'

export interface SnackbarMessage {
  id: number
  state: SnackbarState
  header: string
  text?: string
}

interface ApplicationState {
  activeActionTab: ActionMenuTabs
  snackbarMessages: SnackbarMessage[]
  selectedText: TextState | null
  /** Represents the text state in the edit text overlay */
  currentEditingText: TextState | null
  selectedSticker: StickerState | null
  editTextState: 'none' | 'editing' | 'adding'
  isBackgroundLoading: boolean
  isStickerLoading: boolean,
  isPreviewCanvasLoaded: boolean,
  isActionMenuLoaded: boolean,
  disableEffectsOnPreview: boolean,
  clipboardData: DisplayObjectState | null
  hasEnoughMemory: boolean
}

// Section tabs
export enum ActionMenuTabs {
  BACKGROUND,
  TEXT,
  STICKERS,
  EFFECTS
}

const { getId } = useIdGenerator()

export const useApplicationStore = defineStore('application', {
  state: (): ApplicationState => ({
    activeActionTab: ActionMenuTabs.BACKGROUND,
    snackbarMessages: [],
    selectedText: null,
    currentEditingText: null,
    selectedSticker: null,
    editTextState: 'none',
    isBackgroundLoading: false,
    isStickerLoading: false,
    isPreviewCanvasLoaded: false,
    isActionMenuLoaded: false,
    disableEffectsOnPreview: false,
    clipboardData: null,
    hasEnoughMemory: true
  }),
  actions: {
    /**
     * Push a snackbar message optionally with an expiry
     * @param message The message to push
     * @param expiry Expiry in seconds, or null if no expiry
     */
    pushSnackbarMessage (message: Omit<SnackbarMessage, 'id'>, expiry: number | null = 5): void {
      const id = getId()
      this.snackbarMessages.push({
        id,
        ...message
      })
  
      if (expiry !== null) {
        setTimeout(() => {
          this.dismissSnackbarMessage(id)
        }, expiry * 1000)
      }
    },
    /**
     * Dismiss a snackbar message
     * @param messageId The ID of the message to dismiss
     */
    dismissSnackbarMessage (messageId: number): void {
      const findIndex = this.snackbarMessages.findIndex(msg => msg.id === messageId)
      if (findIndex != -1) {
        this.snackbarMessages.splice(findIndex, 1)
      }
    },
    /**
     * Clear all snackbar messages
     */
    clearSnackbarMessages (): void {
      while (this.snackbarMessages.length > 0) {
        this.snackbarMessages.pop()
      }
    },
    /** Deselects all selected layers */
    deselectAllEntities (): void {
      this.selectedSticker = null
      this.selectedText = null
    },
    /** Sets the selected item to the sticker */
    selectSticker (stickerState: StickerState): void {
      this.deselectAllEntities()
      this.selectedSticker = stickerState
    },
    /** Sets the selected item to the text */
    selectText (textState: TextState): void {
      this.deselectAllEntities()
      this.selectedText = textState
    }
  }
})
